import { getAPIErrorReason , transformWorkloadReportData } from '../helpers/common';
import axios from 'axios';
import dayjs from 'dayjs';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import { captureException } from './logService';
import { rootStore } from '../mobx/rootStore';
import { WorkloadReportApiResponseInterface } from '../interfaces/ReportInterface';

export const getPaymentReportData = async (workspace_id: string, start_date: Date, end_date: Date) => {
  try {
    rootStore.reportStore.setLoading(true);
    const params = {
      companyId: workspace_id,
      start_date: dayjs(start_date).format('YYYY-MM-DD'),
      end_date: dayjs(end_date).format('YYYY-MM-DD')
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/report/paymentReport`, { params });
    rootStore.reportStore.setPaymentReportData(response?.data);
    return response?.data;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get payment report data please try again';
    captureException(error);
    dispatchReportError(error);
    return undefined;
  } finally {
    rootStore.reportStore.setLoading(false);
  }
};

export const getInvoiceReportData = async (workspace_id: string) => {
  try {
    rootStore.reportStore.setLoading(true);
    const params = {
      companyId: workspace_id
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/report/invoiceReport`, { params });
    rootStore.reportStore.setInvoiceReportData(response?.data);
    return response?.data;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get Invoice report data please try again';
    captureException(error);
    dispatchReportError(error);
    return undefined;
  } finally {
    rootStore.reportStore.setLoading(false);
  }
};

export const getProjectReportData = async (workspace_id: string | undefined) => {
  try {
    if (!workspace_id) return;
    rootStore.reportStore.setLoading(true);
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    const params = {
      companyId: workspace_id,
      userId: userDetails?.id
    };

    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/report/projectReport`, { params });

    //Set project report data in mobx store
    rootStore.reportStore.setProjectReportData(response?.data);

    return response?.data;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get Project report data please try again';
    captureException(error);
    dispatchReportError(error);
    return undefined;
  } finally {
    rootStore.reportStore.setLoading(false);
  }
};

export const getWorkloadReportData = async (
  userIds: string[],
  startDate: string,
  endDate: string,
  regularHoursPerDay: number = 8
) => {
  try {
    rootStore.reportStore.setLoading(true);

    const payload = {
      userIds: userIds,
      startDate: startDate,
      endDate: endDate,
      regularHoursPerDay: regularHoursPerDay
    };

    const response = await axios.post<WorkloadReportApiResponseInterface[]>(
      `${REACT_APP_TEAMCAMP_APIURL}/report/getWorkloadReport`,
      payload
    );

    const apiData = response?.data || [];

    // Transform API data to component format
    const workspaceUsers = rootStore.workspaceStore.currentWorkspace?.users || [];
    const transformedData = transformWorkloadReportData(
      apiData,
      workspaceUsers,
      startDate,
      endDate,
      regularHoursPerDay
    );

    // Store the transformed workload report data in MobX store
    rootStore.reportStore.setWorkloadReportData(transformedData);

    return transformedData;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get workload report data please try again';
    captureException(error);
    dispatchReportError(error);
    return undefined;
  } finally {
    rootStore.reportStore.setLoading(false);
  }
};

//get team timeline activities
export const getActivities = async (
  payload: { users?: string[]; projects?: string[] },
  startDate: string,
  endDate: string
) => {
  const params = {
    pageSize: 100,
    cursor: rootStore.reportStore.reportTimelineNextCursor ? rootStore.reportStore.reportTimelineNextCursor : undefined,
    from: startDate,
    to: endDate
  };
  try {
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/activity/getWholeCompanyActivityList`, payload, {
      params: params
    });
    if (response) {
      rootStore.reportStore.setReportTimelineNextCursor(response?.data?.pageInfo?.nextCursor);
      return response;
    }
    return false;
  } catch (error) {
    console.log('Error fetching data:', error);
    return false;
  }
};

function dispatchReportError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}
